import { FC, useCallback } from 'react';
import { ModalRouteProps } from 'context/ModalRouter';
import { AssociateProfileModal } from 'app/modules/posts/components/PostsAssociateProfileModal';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import { useAnalyticsInventory } from 'app/modules/analytics/hooks/useAnalyticsInventory';
import { useAnalyticsHealthQuery } from 'app/modules/analytics/hooks/useAnalyticsHealthQuery';
import { NumberParam, useQueryParams } from 'use-query-params';
import { useTranslation } from 'react-i18next';
import { useToast } from 'app/hooks/useToast';
import Logger from 'utils/logger';
import {
  buildAnalyticsReconnectOutcomeLog,
  buildAnalyticsReconnectToastId,
  classifyAnalyticsReconnectOutcome,
} from 'app/modules/analytics/utils/analyticsReconnectOutcome';

const AnalyticsAssociateProfileModal: FC<ModalRouteProps> = (props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { pages } = useAnalyticsInventory();
  const { markAwaitingCreatedProfile } = useAnalyticsSelection();
  const { getPageHealth, refetchPageHealth } = useAnalyticsHealthQuery();
  const [query] = useQueryParams({
    id: NumberParam,
  });
  const reconnectedPageId = query.id ?? null;

  const verifyReconnectOutcome = useCallback(
    async (pageId: number) => {
      const healthBeforeReconnect = getPageHealth(pageId);
      const healthAfterReconnect = await refetchPageHealth(pageId);
      const outcome = classifyAnalyticsReconnectOutcome(healthAfterReconnect);

      Logger.log(
        'Analytics reconnect outcome',
        buildAnalyticsReconnectOutcomeLog({
          pageId,
          outcome,
          healthBeforeReconnect,
          healthAfterReconnect,
        }),
      );

      if (outcome !== 'still-expired') {
        return;
      }

      const profileName =
        healthAfterReconnect?.page_name ??
        pages.find((page) => page.id === pageId)?.name ??
        '';

      toast(
        t('analytics.reconnectDidNotTakeEffect', { profileName }),
        'error',
        {
          id: buildAnalyticsReconnectToastId(pageId, Date.now()),
        },
      );
    },
    [getPageHealth, refetchPageHealth, pages, toast, t],
  );

  return (
    <AssociateProfileModal
      {...props}
      onProfileAdded={() => {
        markAwaitingCreatedProfile();

        if (reconnectedPageId !== null) {
          verifyReconnectOutcome(reconnectedPageId);
        }
      }}
    />
  );
};

export default AnalyticsAssociateProfileModal;
